import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Hero with:
 *  1) Static base halftone dot background (SVG data URL, computed once)
 *  2) Static three-layer chromatic aberration on the title
 *     (cyan / dark / magenta -- persistent misregistration look)
 *  3) A cursor-following "hover-glow" layer that renders a CRISP
 *     dot lattice via a repeating radial-gradient (NOT a blur, NOT a
 *     soft glow, NOT a color-block gradient). A radial-gradient mask-image
 *     driven by --mx / --my controls transparency falloff around the
 *     cursor, so individual dots stay pixel-sharp -- they just get
 *     progressively hidden by the mask as distance grows.
 *  4) A pink text-reveal layer that shares the SAME radial-gradient mask
 *     shape (using the same --mx / --my variables, cascaded through the
 *     h1) so the dot cluster and the pink text focus region overlap
 *     exactly.
 */

const TITLE_L1 = "Bold digital";
const TITLE_L2 = "experiences for";
const TITLE_L3 = "brands that";
const TITLE_L4 = "refuse to fade.";

export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);

  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    const touchOnly =
      typeof window !== "undefined" &&
      window.matchMedia("(hover: none), (pointer: coarse)").matches;
    setIsTouchDevice(touchOnly);
  }, []);

  // Base gray halftone tile for the whole hero background.
  const dotBgStyle = useMemo(() => {
    const size = 6;
    const r = 1;
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'><circle cx='${size / 2}' cy='${size / 2}' r='${r}' fill='%23000000' fill-opacity='0.06'/></svg>`;
    const encoded = encodeURIComponent(svg)
      .replace(/'/g, "%27")
      .replace(/"/g, "%22");
    return {
      backgroundColor: "#EDEDED",
      backgroundImage: `url("data:image/svg+xml;utf8,${encoded}")`,
      backgroundRepeat: "repeat",
      backgroundSize: `${size}px ${size}px`,
    } as const;
  }, []);

  useEffect(() => {
    if (isTouchDevice) return;
    const hero = heroRef.current;
    const title = titleRef.current;
    if (!hero || !title) return;

    hero.style.setProperty("--hover-opacity", "0");

    // Mouse coords, hero-local and title-local. Start offscreen so
    // nothing shows before first move.
    let mouseHeroX = -9999;
    let mouseHeroY = -9999;
    let mouseTitleX = -9999;
    let mouseTitleY = -9999;

    const onMove = (e: PointerEvent) => {
      const heroRect = hero.getBoundingClientRect();
      const titleRect = title.getBoundingClientRect();
      mouseHeroX = e.clientX - heroRect.left;
      mouseHeroY = e.clientY - heroRect.top;
      mouseTitleX = e.clientX - titleRect.left;
      mouseTitleY = e.clientY - titleRect.top;
      hero.style.setProperty("--hover-opacity", "1");
    };

    const onLeave = () => {
      hero.style.setProperty("--hover-opacity", "0");
    };

    hero.addEventListener("pointermove", onMove);
    hero.addEventListener("pointerleave", onLeave);

    // Smooth 1D pseudo-noise via layered sines. Returns a value in
    // [0,1) that varies CONTINUOUSLY (no per-frame flicker). The
    // previous hash-based noise (`sin(x*12.9898)*43758.5453`) produced
    // uncorrelated values between adjacent frames, which showed up as
    // a very visible "strobe/flash" on both hue and blob position.
    // This layered-sine version is fully differentiable -> silky.
    const noise1D = (x: number) => {
      const s =
        Math.sin(x * 1.3) * 0.5 +
        Math.sin(x * 2.7 + 1.1) * 0.3 +
        Math.sin(x * 4.1 + 2.3) * 0.2;
      return s * 0.5 + 0.5;
    };

    // Five blobs, each with:
    //   - a base orbit (sin/cos) with LARGE amplitude and
    //     WILDLY different frequencies for x vs y and blob vs blob
    //   - an additive low-frequency noise jitter with its own
    //     time offset so no two blobs ever sync up
    // The union of the 5 circles (mask-composite: add), scaled by a
    // slow global "breathe", yields a cloud/lava-lamp edge.
    type Blob = {
      fx: number; // x sin frequency
      fy: number; // y cos frequency
      ax: number; // x amplitude (px)
      ay: number; // y amplitude (px)
      phx: number; // x phase
      phy: number; // y phase
      nxOff: number; // noise sample offset x
      nyOff: number; // noise sample offset y
      nAmp: number; // noise amplitude (px)
    };
    // Per-blob `lerp` controls how quickly that blob's tracking
    // position catches up to the mouse each frame (0..1, larger =
    // faster). Different lerps -> lagging blobs form a natural
    // comet-like trail behind fast pointer moves.
    // dragBoost: how strongly this blob gets flung by the pointer's
    // instantaneous velocity (bigger = trails further on fast moves).
    // Leader stays near 0 (glued to cursor). Tail blobs get big boosts
    // so quick pointer swipes fling them backward like a comet tail.
    const blobs: (Blob & { lerp: number; dragBoost: number })[] = [
      {
        fx: 0.4, fy: 0.3, ax: 45, ay: 40,
        phx: 0, phy: 0,
        nxOff: 0, nyOff: 10, nAmp: 30,
        lerp: 0.32, dragBoost: 0,
      },
      {
        fx: 1.1, fy: 0.95, ax: 55, ay: 50,
        phx: 1.7, phy: 2.4,
        nxOff: 23, nyOff: 41, nAmp: 28,
        lerp: 0.2, dragBoost: 0.6,
      },
      {
        fx: 0.25, fy: 0.5, ax: 38, ay: 44,
        phx: 3.2, phy: 4.1,
        nxOff: 67, nyOff: 88, nAmp: 36,
        lerp: 0.1, dragBoost: 1.3,
      },
      {
        fx: 0.7, fy: 0.18, ax: 52, ay: 40,
        phx: 5.8, phy: 0.9,
        nxOff: 112, nyOff: 145, nAmp: 32,
        lerp: 0.05, dragBoost: 2.1,
      },
      {
        fx: 1.4, fy: 0.6, ax: 46, ay: 52,
        phx: 2.6, phy: 4.9,
        nxOff: 190, nyOff: 220, nAmp: 36,
        lerp: 0.02, dragBoost: 3.0,
      },
    ];

    // Each blob has its own tracking position that eases toward the
    // current mouse coords. Init offscreen.
    const trackHero = blobs.map(() => ({ x: -9999, y: -9999 }));
    const trackTitle = blobs.map(() => ({ x: -9999, y: -9999 }));

    // Global "breathe" -- slow overall expansion/contraction of the
    // whole reveal region on top of per-blob motion.
    const BREATHE_PERIOD = 3.4; // seconds
    const BREATHE_MIN = 0.85;
    const BREATHE_MAX = 1.15;

    // FULL-SPECTRUM hue drift -- the cloud slowly cycles through the
    // entire color wheel (magenta -> red -> orange -> yellow-green ->
    // cyan -> blue -> violet -> back). Period ~28s so it feels ambient
    // rather than flashy. Noise adds a small non-uniform wobble so it
    // doesn't feel like a clockwise sweep.
    const HUE_PERIOD = 28; // seconds for a full 360deg cycle
    const HUE_NOISE_AMP = 18;
    const HUE_NOISE_FREQ = 0.13;
    const SAT_BASE = 92;
    const SAT_AMP = 6;
    const LIGHT_BASE = 60;
    const LIGHT_AMP = 4;

    // Track mouse velocity between frames -> used to fling tail blobs
    // backward along the movement vector (stronger "wind drag").
    let prevMouseHeroX = mouseHeroX;
    let prevMouseHeroY = mouseHeroY;
    let prevMouseTitleX = mouseTitleX;
    let prevMouseTitleY = mouseTitleY;
    let velHeroX = 0;
    let velHeroY = 0;
    let velTitleX = 0;
    let velTitleY = 0;

    let time = 0;
    let rafId = 0;
    const tick = () => {
      time += 0.016;

      // Global breathe: 0.85 -> 1.15 -> 0.85, period ~3.4s
      const breathePhase = (time * (2 * Math.PI)) / BREATHE_PERIOD;
      const breathe01 = (Math.sin(breathePhase) + 1) / 2; // 0..1
      const scale =
        BREATHE_MIN + (BREATHE_MAX - BREATHE_MIN) * breathe01;
      hero.style.setProperty("--blob-scale", scale.toFixed(3));

      // Full-spectrum hue cycle -- gas that changes color as it drifts.
      const hue =
        (time * 360) / HUE_PERIOD +
        (noise1D(time * HUE_NOISE_FREQ) - 0.5) * HUE_NOISE_AMP;
      const sat =
        SAT_BASE + Math.sin(time * 0.4 + 1.3) * SAT_AMP;
      const light =
        LIGHT_BASE + Math.sin(time * 0.5 + 0.7) * LIGHT_AMP;
      hero.style.setProperty(
        "--dot-color",
        `hsl(${hue.toFixed(1)}, ${sat.toFixed(1)}%, ${light.toFixed(1)}%)`,
      );

      // Instantaneous mouse velocity (per-frame delta), lightly smoothed.
      // Guard against the -9999 offscreen init so first move doesn't
      // register a huge phantom velocity.
      if (
        prevMouseHeroX > -9000 &&
        mouseHeroX > -9000
      ) {
        const dvx = mouseHeroX - prevMouseHeroX;
        const dvy = mouseHeroY - prevMouseHeroY;
        const dtx = mouseTitleX - prevMouseTitleX;
        const dty = mouseTitleY - prevMouseTitleY;
        // low-pass filter -> velocity decays smoothly when mouse stops
        velHeroX = velHeroX * 0.78 + dvx * 0.22;
        velHeroY = velHeroY * 0.78 + dvy * 0.22;
        velTitleX = velTitleX * 0.78 + dtx * 0.22;
        velTitleY = velTitleY * 0.78 + dty * 0.22;
      }
      prevMouseHeroX = mouseHeroX;
      prevMouseHeroY = mouseHeroY;
      prevMouseTitleX = mouseTitleX;
      prevMouseTitleY = mouseTitleY;

      for (let i = 0; i < blobs.length; i++) {
        const b = blobs[i];
        const dx =
          Math.sin(time * b.fx + b.phx) * b.ax +
          (noise1D(time * 0.15 + b.nxOff) - 0.5) * b.nAmp;
        const dy =
          Math.cos(time * b.fy + b.phy) * b.ay +
          (noise1D(time * 0.17 + b.nyOff) - 0.5) * b.nAmp;

        // Ease each blob's tracking position toward the current
        // mouse. Different lerp values -> heavier blobs lag behind
        // -> natural trailing/comet effect on quick moves.
        const th = trackHero[i];
        const tt = trackTitle[i];
        if (th.x === -9999) {
          th.x = mouseHeroX;
          th.y = mouseHeroY;
          tt.x = mouseTitleX;
          tt.y = mouseTitleY;
        } else {
          th.x += (mouseHeroX - th.x) * b.lerp;
          th.y += (mouseHeroY - th.y) * b.lerp;
          tt.x += (mouseTitleX - tt.x) * b.lerp;
          tt.y += (mouseTitleY - tt.y) * b.lerp;
        }

        // Wind-drag offset: fling each blob BACKWARD along the mouse's
        // instantaneous velocity, scaled by that blob's dragBoost.
        // Leader (boost=0) is unaffected; tail blobs get slung further
        // the faster the pointer moves -> visible comet tail.
        const heroBx = th.x + dx - velHeroX * b.dragBoost;
        const heroBy = th.y + dy - velHeroY * b.dragBoost;
        const titleBx = tt.x + dx - velTitleX * b.dragBoost;
        const titleBy = tt.y + dy - velTitleY * b.dragBoost;

        const idx = i + 1;
        hero.style.setProperty(`--blob${idx}-x`, `${heroBx.toFixed(2)}px`);
        hero.style.setProperty(`--blob${idx}-y`, `${heroBy.toFixed(2)}px`);
        title.style.setProperty(`--blob${idx}-x`, `${titleBx.toFixed(2)}px`);
        title.style.setProperty(`--blob${idx}-y`, `${titleBy.toFixed(2)}px`);
      }

      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      hero.removeEventListener("pointermove", onMove);
      hero.removeEventListener("pointerleave", onLeave);
    };
  }, [isTouchDevice]);

  // Shared title inner content (rendered 4 times to stack layers precisely)
  const titleContent = (
    <>
      {TITLE_L1}
      <br />
      {TITLE_L2}
      <br />
      {TITLE_L3}
      <br />
      {TITLE_L4}
    </>
  );

  const titleFont =
    "'Space Grotesk', 'Inter', ui-sans-serif, system-ui, sans-serif";

  return (
    <section
      ref={heroRef}
      className="relative w-full h-screen min-h-[720px] overflow-hidden"
      style={dotBgStyle}
    >
      {/* Hover-glow layer: crisp pink dot lattice, revealed by radial mask.
          Sits behind text content, above the base halftone bg. */}
      <div
        aria-hidden="true"
        className="hover-glow pointer-events-none absolute inset-0 z-10"
      />

      {/* Content */}
      <div className="relative z-20 flex h-full w-full items-center justify-center px-6">
        <div className="max-w-5xl text-center">
          <p
            className="mb-6 text-xs md:text-sm tracking-[0.35em] uppercase text-neutral-500"
            style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
          >
            Studio 07 &mdash; Est. 2019
          </p>

          <h1
            ref={titleRef}
            className="relative inline-block select-none text-[44px] leading-[1.05] md:text-[88px] lg:text-[112px] font-black tracking-tight"
            style={{ fontFamily: titleFont }}
          >
            {/* Cyan layer (offset -2px). Base opacity 40%, boosted up
                to ~90% while hovering via --hover-opacity so the
                chromatic misregistration "shadow" darkens on hover. */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 block hero-aberration-cyan"
              style={{
                color: "#00C8D4",
                mixBlendMode: "multiply",
              }}
            >
              {titleContent}
            </span>

            {/* Magenta layer (offset +2px). Same hover-driven darkening. */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 block hero-aberration-magenta"
              style={{
                color: "#FF2D78",
                mixBlendMode: "multiply",
              }}
            >
              {titleContent}
            </span>

            {/* Main dark layer (relative -- defines box + always crisp on top) */}
            <span className="relative block" style={{ color: "#1A1A1A" }}>
              {titleContent}
            </span>

            {/* Reveal layer: solid #FF2D78 text, masked by the SAME radial
                gradient shape as .hover-glow (same --mx/--my scale, driven by
                the title's local mouse coords). This makes the pink-text
                focus region and the dot-cluster region overlap perfectly. */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 block hero-reveal-layer"
              style={{ color: "var(--dot-color, #FF2D78)" }}
            >
              {titleContent}
            </span>
          </h1>

          <p className="mx-auto mt-10 max-w-2xl text-base md:text-lg text-neutral-600 leading-relaxed">
            We craft interfaces and interactions for teams who care about the
            details others skip. Precision code, expressive motion, no shortcuts.
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <a
              href="#work"
              className="whitespace-nowrap inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] px-6 py-3 text-sm font-medium text-white cursor-pointer transition-colors hover:bg-[#FF2D78]"
            >
              View selected work
              <i className="ri-arrow-right-up-line text-base w-4 h-4 flex items-center justify-center"></i>
            </a>
            <a
              href="#contact"
              className="whitespace-nowrap inline-flex items-center gap-2 rounded-full border border-neutral-900/20 bg-white/60 backdrop-blur px-6 py-3 text-sm font-medium text-neutral-900 cursor-pointer transition-colors hover:bg-white"
            >
              Start a project
              <i className="ri-mail-line text-base w-4 h-4 flex items-center justify-center"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Bottom meta bar */}
      <div className="absolute inset-x-0 bottom-0 z-20 px-6 md:px-10 pb-6">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-4 text-xs md:text-sm text-neutral-500">
          <div
            className="flex items-center gap-2"
            style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
          >
            <span className="inline-block w-2 h-2 rounded-full bg-[#FF2D78]"></span>
            Available for Q3 &mdash; 2 slots left
          </div>
          <div
            className="flex flex-wrap items-center gap-x-6 gap-y-2"
            style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
          >
            <span>Brand systems</span>
            <span>Interaction design</span>
            <span>Full-stack builds</span>
            <span>Motion &amp; 3D</span>
          </div>
          <div
            className="text-neutral-400"
            style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
          >
            (Move your cursor across the headline)
          </div>
        </div>
      </div>
    </section>
  );
}